import { Button, Col, Row } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Loader } from "~Common/Components";
import Page from "../../Common/Page";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputs, StyledFormSelect } from "~components/Form/StyledFormInputs.tsx";
import { useCreateTeamGroup, useFetchTeamGroup, useUpdateTeamGroup } from "~entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { IClub } from "./types";
import { noLeadingOrTrailingSpacesReactHookFormAdapter } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.ENTITY_GROUP;
const statuses = ["ACTIVE", "PENDING", "INACTIVE"];

type IFormInput = Pick<
  IClub,
  "nameFullLocal" | "nameFullLatin" | "codeLatin" | "codeLocal" | "status" | "internationalReference"
>;
const ManageClub = () => {
  const { sport, organizationId, clubId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { data, isLoading: isFetchLoading } = useFetchTeamGroup();
  const { mutateAsync: createFn, isPending: isCreatePending } = useCreateTeamGroup();
  const { mutate: updateFn, isPending: isUpdatePending } = useUpdateTeamGroup();

  useEffect(() => {
    reset({
      nameFullLocal: data?.nameFullLocal,
      nameFullLatin: data?.nameFullLatin,
      codeLatin: data?.codeLatin,
      codeLocal: data?.codeLocal,
      status: data?.status,
      internationalReference: data?.internationalReference,
    });
  }, [data]);

  const createClub = useCallback(
    (data: IFormInput) => {
      // @ts-ignore
      createFn(data).then(() => navigate(`/${customerId}/admin/${sport}/${organizationId}/entity-groups`, { state: { search: data.nameFullLocal }}));
    },
    [createFn, data?.nameFullLocal, customerId, sport, organizationId],
  );

  const updateClub = useCallback(
    (data: IFormInput) => {
      // @ts-ignore
      updateFn(data);
    },
    [updateFn],
  );

  const title = useMemo(() => {
    return clubId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data?.nameFullLocal ? `- ${data?.nameFullLocal}` : ""}`
      : `${formatMessage({
          id: "addClub",
          defaultMessage: "Adding a New Club",
        })}`;
  }, [clubId, formatMessage, data]);

  const onSubmit: SubmitHandler<IFormInput> = useCallback(
    (data) => {
      if (clubId) {
        updateClub(data);
      } else {
        createClub(data);
      }
    },
    [clubId, updateClub, createClub],
  );

  return (
    <Page title={title} tabTitle={title}>
      {isUpdatePending || isCreatePending || isFetchLoading ? (
        <Loader />
      ) : (
        <Content>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local.full"
                      name="nameFullLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "club",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "nameFullLocal",
                            }),
                          },
                        ),
                        minLength: {
                          value: 2,
                          message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                        },
                        validate: {
                          noLeadingOrTrailingSpaces: noLeadingOrTrailingSpacesReactHookFormAdapter(formatMessage),
                        },
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="name.local.code"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.code.item",
                        },
                        {
                          item: formatMessage({
                            id: "club",
                          }),
                        },
                      )}
                      name="codeLocal"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <FormControl
                  labelText="status"
                  name="status"
                  rules={{
                    required: formatMessage(
                      { id: "form.help.required.field" },
                      {
                        field: formatMessage({
                          id: "club",
                        }),
                      },
                    ),
                  }}
                  control={control}
                  labelTooltip={formatMessage({ id: "form.help.status.item" }, { item: formatMessage({ id: "club" }) })}
                  render={({ field }) => (
                    <StyledFormSelect
                      style={{ width: "100%" }}
                      {...field}
                      options={statuses.map((status) => ({
                        value: status,
                        label: status,
                      }))}
                    />
                  )}
                  errors={errors}
                />
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin.full"
                      name="nameFullLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.full.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "club",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="name.latin.code"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.code.item",
                        },
                        {
                          item: formatMessage({
                            id: "club",
                          }),
                        },
                      )}
                      name="codeLatin"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <FormControl
                  labelText="internationalReference"
                  name="internationalReference"
                  control={control}
                  labelTooltip={formatMessage(
                    {
                      id: "form.help.international.reference.item",
                    },
                    { item: formatMessage({ id: "club" }) },
                  )}
                  render={({ field }) => <StyledFormInputs {...field} />}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 20 }}>
              <PermissionCheck
                permissionKeys={[PermissionName]}
                permissionOptions={clubId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
              >
                <Button htmlType="submit" type="primary">
                  {clubId ? formatMessage({ id: "club.update" }) : formatMessage({ id: "club.add" })}
                </Button>
              </PermissionCheck>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/entity-groups`}>
                <Button style={{ marginLeft: 30 }} type="primary">
                  {formatMessage({ id: "close" })}
                </Button>
              </Link>
            </Row>
          </form>
        </Content>
      )}
    </Page>
  );
};

const Content = styled.div`
  display: flex;

  .container {
    margin: 0;
  }
`;
export default ManageClub;
